import { SportName } from '@shared/enums/SportName';
import { TagName } from '@shared/enums/TagName';

/**
 * Service for handling HighlightIQ formulas and calculations
 *
 * iqScores explanation (each score is a number between 0 and 10):
 * audioScore: Measures how loud the moment was (e.g., crowd reaction, commentator excitement)
 * eventScore: A fixed score based on what type of event happened (goal, miss, save, etc.)
 * timingScore: Scores the importance of a moment based on when it happened during the game
 * scoringScore: Measures how close the game was at the moment of the tag
 *
 */

export const FORMULA_TYPES = {
	MATCH_HIGHLIGHTS: 'match_highlights',
	CONDENSED_GAME:   'condensed_game',
	HIGH_EMOTION:     'high_emotion',
	TENSE_MOMENTS:    'tense_moments',
};

// Formula configurations for different sports
const SPORT_FORMULAS = {
	[SportName.HANDBALL]: {
		[FORMULA_TYPES.MATCH_HIGHLIGHTS]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, timingScore, scoringScore } = tag.iqScore;
				return 0.3 * audioScore + 0.4 * eventScore + 0.3 * ((timingScore * scoringScore) / 10);
			},
			balanceTeams:          true,
			balanceFactor:         0.5,
			includeExtraTags:      [],
			includeLastGoal:       true,
			includeEventStart:     true,
			includeEventEnd:       true,
			defaultClipCount:      10,
			defaultTargetDuration: 150,
			distributeGamePeriods: false,
			blocksPerPeriod:       3,
			periodMarkers:         [
				{
					start:          TagName.TIMING_HALFTIME_START,
					end:            TagName.TIMING_HALFTIME_END,
					includeMarkers: {
						start: 'first',
						end:   'last',
					},
				},
			],
		},
		[FORMULA_TYPES.CONDENSED_GAME]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, scoringScore } = tag.iqScore;
				return 0.3 * audioScore + 0.6 * eventScore + 0.1 * scoringScore;
			},
			balanceTeams:          true,
			balanceFactor:         0.5,
			includeExtraTags:      [],
			includeLastGoal:       true,
			includeEventStart:     true,
			includeEventEnd:       true,
			defaultClipCount:      32,
			defaultTargetDuration: 480, // 8 minutes in seconds
			distributeGamePeriods: true,
			blocksPerPeriod:       3,
			periodMarkers:         [
				{
					start:          TagName.TIMING_HALFTIME_START,
					end:            TagName.TIMING_HALFTIME_END,
					includeMarkers: {
						start: 'first',
						end:   'last',
					},
				},
			],
			fallbackBlockLength: 10 * 60, // 10 minutes in seconds
		},
		[FORMULA_TYPES.HIGH_EMOTION]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore } = tag.iqScore;
				return 0.5 * audioScore + 0.5 * eventScore;
			},
			balanceTeams:          false,
			includeExtraTags:      [],
			defaultClipCount:      10,
			defaultTargetDuration: 150,
		},
		[FORMULA_TYPES.TENSE_MOMENTS]: {
			formula: (tag) => {
				if (!tag.iqScore) return 0;
				const { audioScore, eventScore, timingScore, scoringScore } = tag.iqScore;
				return 0.2 * audioScore + 0.3 * eventScore + 0.5 * ((timingScore * scoringScore) / 10);
			},
			balanceTeams:          false,
			includeExtraTags:      [],
			defaultClipCount:      10,
			defaultTargetDuration: 150,
		},
	},
};

/**
 * Check if a sport has HighlightIQ formulas
 * @param {String} sportName
 * @returns {Boolean}
 */
export const hasSportFormulas = (sportName) => {
	return !!SPORT_FORMULAS[sportName];
};

/**
 * Get the formulas for a given sport
 * @param {String} sportName
 * @returns {Object|null}
 */
export const getSportFormulas = (sportName) => {
	return SPORT_FORMULAS[sportName] || null;
};

/**
 * Get the default clip count for a formula
 * @param {String} sportName
 * @param {String|Object} formulaType
 * @returns {Number}
 */
export const getDefaultClipCount = (sportName, formulaType) => {
	const formulas = getSportFormulas(sportName);
	// Use formulaType.value if it's an object, otherwise use formulaType directly
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;
	return formulas?.[formulaKey]?.defaultClipCount || 10;
};

/**
 * Get the default target duration for a formula (in seconds)
 * @param {String} sportName
 * @param {String|Object} formulaType
 * @returns {Number}
 */
export const getDefaultTargetDuration = (sportName, formulaType) => {
	const formulas = getSportFormulas(sportName);
	// Use formulaType.value if it's an object, otherwise use formulaType directly
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;
	return formulas?.[formulaKey]?.defaultTargetDuration || 150;
};

/**
 * Check if teams have adequate distribution for team-based formulas
 * @param {Array} tags
 * @returns {Boolean}
 */
export const hasTeamDistribution = (tags) => {
	const teamIds = new Set(tags.map(tag => tag.teamId).filter(Boolean));
	return teamIds.size >= 2;
};

/**
 * Calculate scores for tags based on the formula
 * @param {Array} tags - Tags to calculate scores for
 * @param {String} sportName - Name of the sport
 * @param {String} formulaType - Type of formula to use
 * @returns {Array} Tags with calculated scores
 */
export const calculateTagScores = (tags, sportName, formulaType, customFormulaConfig = null) => {
	const formulas = getSportFormulas(sportName);
	// Use formulaType.value if it's an object, otherwise use formulaType directly
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;

	// If a customFormulaConfig is provided, use it directly. Otherwise, look up from SPORT_FORMULAS.
	const formulaConfigToUse = customFormulaConfig || (formulas ? formulas[formulaKey] : null);

	if (!formulaConfigToUse || !formulaConfigToUse.formula) return tags.map(tag => ({ ...tag, calculatedScore: 0 })); // Return tags with 0 score if no formula

	// const formulaConfig = formulas[formulaKey];

	return tags.map(tag => ({
		...tag,
		calculatedScore: formulaConfigToUse.formula(tag),
	}));
};

/**
 * Helper function to get the appropriate period markers based on configuration
 * @param {Array} tags - All available tags
 * @param {Object} periodConfig - Period configuration
 * @returns {Object} Start and end markers
 */
const getPeriodMarkers = (tags, periodConfig) => {
	// Sort all tags by timestamp first to ensure correct order
	const sortedTags = [...tags].sort((a, b) => a.timestamp - b.timestamp);

	// Find all start and end markers in chronological order
	const startMarkers = sortedTags.filter(tag => tag.tag?.name === periodConfig.start);
	const endMarkers = sortedTags.filter(tag => tag.tag?.name === periodConfig.end);

	if (!startMarkers.length || !endMarkers.length) {
		return { start: null, end: null };
	}

	// Get the appropriate markers based on configuration
	let startMarker = null;
	let endMarker = null;

	// For start markers, we want the first one
	if (periodConfig.includeMarkers?.start === 'last') {
		startMarker = startMarkers[startMarkers.length - 1];
	} else {
		startMarker = startMarkers[0];
	}

	// For end markers, we want the last one
	if (periodConfig.includeMarkers?.end === 'first') {
		endMarker = endMarkers[0];
	} else {
		endMarker = endMarkers[endMarkers.length - 1];
	}

	// Validate that the markers are in the correct order
	if (startMarker && endMarker && startMarker.timestamp > endMarker.timestamp) {
		// If markers are in wrong order, try to find the correct pair
		const validStartMarkers = startMarkers.filter(start =>
			endMarkers.some(end => end.timestamp > start.timestamp)
		);
		const validEndMarkers = endMarkers.filter(end =>
			startMarkers.some(start => start.timestamp < end.timestamp)
		);

		if (validStartMarkers.length && validEndMarkers.length) {
			startMarker = periodConfig.includeMarkers?.start === 'last'
				? validStartMarkers[validStartMarkers.length - 1]
				: validStartMarkers[0];

			endMarker = periodConfig.includeMarkers?.end === 'first'
				? validEndMarkers[0]
				: validEndMarkers[validEndMarkers.length - 1];
		} else {
			// If no valid pairs found, return null
			return { start: null, end: null };
		}
	}

	return { start: startMarker, end: endMarker };
};

/**
 * Returns amount of clips for the winning team based on the goal difference and the factor
 * @param {Number} totalClips - Total number of clips
 * @param {Number} goalTeamA - Number of goals for team A
 * @param {Number} goalTeamB - Number of goals for team B
 * @param {Number} factor - Factor for the dynamic part
 * @returns {Number} Number of tags for the winning team
 */
const getTagCountForWinningTeam = (totalClips, goalTeamA, goalTeamB, factor) => {
	const maxTore = Math.max(goalTeamA, goalTeamB);
	const minTore = Math.min(goalTeamA, goalTeamB);

	// Avoid division by zero
	if (minTore === 0) return Math.round(0);

	const dynamicPart = (maxTore / minTore - 1) * factor + 1;
	const computedValue = (totalClips / 2) * dynamicPart;
	const result = Math.min(totalClips, computedValue);

	return Math.round(result);
};

/**
 * Select tags based on score imbalance between teams
 * @param {Array} scoredTags - Tags with calculated scores
 * @param {String} limitType - 'clips' or 'seconds'
 * @param {Number} limitValue - Number of clips or seconds
 * @param {Number} goalTeamA - Number of goals for team A
 * @param {Number} goalTeamB - Number of goals for team B
 * @param {Number} factor - Factor for the dynamic part
 * @returns {Array} Selected tags
 */
const selectTagsByScoreImbalance = (scoredTags, limitType, limitValue, goalTeamA, goalTeamB, factor) => {
	// Group tags by team
	const teamTags = {};
	for (const tag of scoredTags) {
		if (!tag.teamId) continue;
		if (!teamTags[tag.teamId]) {
			teamTags[tag.teamId] = [];
		}
		teamTags[tag.teamId].push(tag);
	}

	// Sort each team's tags by score
	const teams = Object.keys(teamTags);
	teams.forEach(teamId => {
		teamTags[teamId].sort((a, b) => b.calculatedScore - a.calculatedScore);
	});

	if (teams.length === 0) return [];
	if (teams.length === 1) {
		const teamId = teams[0];
		return limitType === 'clips'
			? teamTags[teamId].slice(0, limitValue)
			: selectTagsByDuration(teamTags[teamId], limitValue);
	}

	// Determine winning team and calculate tag distribution
	const winningTeamId = goalTeamA > goalTeamB ? teams[0] : teams[1];
	const losingTeamId = winningTeamId === teams[0] ? teams[1] : teams[0];

	if (limitType === 'clips') {
		// Calculate number of clips for winning team
		const winningTeamClips = getTagCountForWinningTeam(
			limitValue,
			goalTeamA,
			goalTeamB,
			factor
		);
		const losingTeamClips = limitValue - winningTeamClips;

		// Select tags for each team
		const selectedTags = [];
		selectedTags.push(...teamTags[winningTeamId].slice(0, winningTeamClips));
		selectedTags.push(...teamTags[losingTeamId].slice(0, losingTeamClips));
		return selectedTags;
	} else {
		// Duration-based selection
		const winningTeamDuration = getTagCountForWinningTeam(
			limitValue,
			goalTeamA,
			goalTeamB,
			factor
		);
		const losingTeamDuration = limitValue - winningTeamDuration;

		const selectedTags = [];
		selectedTags.push(...selectTagsByDuration(teamTags[winningTeamId], winningTeamDuration));
		selectedTags.push(...selectTagsByDuration(teamTags[losingTeamId], losingTeamDuration));
		return selectedTags;
	}
};

/**
 * Select tags up to a target duration
 * @param {Array} tags - Tags to select from
 * @param {Number} targetDuration - Target duration in seconds
 * @returns {Array} Selected tags
 */
const selectTagsByDuration = (tags, targetDuration) => {
	let totalDuration = 0;
	const selected = [];

	for (const tag of tags) {
		const duration = tag.secondsAfter + tag.secondsBefore;
		if (totalDuration + duration > targetDuration) {
			break;
		}
		selected.push(tag);
		totalDuration += duration;
	}

	return selected;
};

/**
 * Helper function to collect extra tags based on formula configuration
 * @param {Array} tags - All available tags
 * @param {Object} formulaConfig - Formula configuration
 * @returns {Array} Array of extra tags
 */
const collectExtraTags = (tags, formulaConfig) => {
	if (!formulaConfig) return [];

	const extraTags = new Set();

	// Extra tags: by name
	if (formulaConfig.includeExtraTags?.length) {
		formulaConfig.includeExtraTags.forEach(tagName => {
			const matchingTags = tags.filter(tag => tag.tag?.name === tagName);
			matchingTags.forEach(tag => extraTags.add(tag));
		});
	}

	// Extra tags: last goal
	if (formulaConfig.includeLastGoal) {
		const goalTagNames = [TagName.ACTION_GOAL, TagName.ACTION_PENALTY_GOAL, TagName.ACTION_OWN_GOAL, TagName.ACTION_7M_GOAL];
		const goalTags = tags.filter(tag => goalTagNames.includes(tag.tag?.name));
		if (goalTags.length) {
			const lastGoal = goalTags.reduce((latest, tag) =>
				tag.timestamp > (latest?.timestamp || -Infinity) ? tag : latest, null
			);
			if (lastGoal) {
				extraTags.add(lastGoal);
			}
		}
	}

	// Extra tags: period markers
	if ((formulaConfig.includeEventStart || formulaConfig.includeEventEnd) && formulaConfig.periodMarkers?.length) {
		formulaConfig.periodMarkers.forEach(periodConfig => {
			const { start, end } = getPeriodMarkers(tags, periodConfig);
			if (start && formulaConfig.includeEventStart) extraTags.add(start);
			if (end && formulaConfig.includeEventEnd) extraTags.add(end);
		});
	}

	return Array.from(extraTags);
};

/**
 * Calculate the number of extra tags that would be included for a given formula configuration
 * @param {Array} tags - All available tags
 * @param {Object} formulaConfig - Formula configuration
 * @returns {Number} Number of extra tags
 */
export const calculateExtraTagsCount = (tags, formulaConfig) => {
	return collectExtraTags(tags, formulaConfig).length;
};

/**
 * Calculate the total duration of extra tags that would be included for a given formula configuration
 * @param {Array} tags - All available tags
 * @param {Object} formulaConfig - Formula configuration
 * @returns {Number} Total duration in seconds
 */
export const calculateExtraTagsDuration = (tags, formulaConfig) => {
	return collectExtraTags(tags, formulaConfig)
		.reduce((total, tag) => total + tag.secondsAfter + tag.secondsBefore, 0);
};

/**
 * Select tags based on the formula configuration
 * @param {Array} tags - All available tags
 * @param {Object} event - Event data
 * @param {String} formulaType - Type of formula to use
 * @param {String} limitType - 'clips' or 'seconds'
 * @param {Number} limitValue - Number of clips or seconds
 * @returns {Array} Selected tags
 */
export const selectTagsWithFormula = (tags, event, formulaType, limitType, limitValue, customFormulaConfig = null) => {
	const sportName = event?.sport?.name;

	if (!sportName || (!hasSportFormulas(sportName) && !customFormulaConfig)) {
		return [];
	}

	const formulas = getSportFormulas(sportName);
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;

	// Use customFormulaConfig if provided, otherwise lookup from SPORT_FORMULAS
	const formulaConfig = customFormulaConfig || (formulas ? formulas[formulaKey] : null);

	if (!formulaConfig) {
		return [];
	}

	// If limit value is 0, return empty selection without showing any error
	if (limitValue === 0) {
		return [];
	}

	// Filter out tags without iqScore
	const tagsWithIQScores = tags.filter(tag => tag.iqScore);

	// Calculate scores for all tags using the effective formula config (either standard or custom)
	const scoredTags = calculateTagScores(tagsWithIQScores, sportName, formulaKey, formulaConfig);

	// Get extra tags using the helper function
	const extraTags = collectExtraTags(tags, formulaConfig);

	// Sort extraTags by timestamp to prioritize them in sequential order
	extraTags.sort((a, b) => a.timestamp - b.timestamp);

	// Handle tags by team if needed
	let selectedTags = [];
	let remainingLimit = limitValue;

	// First add extra tags counting towards the limit
	if (extraTags.length > 0) {
		if (limitType === 'clips') {
			// For clip limit, take as many extra tags as will fit
			const extraTagsToInclude = extraTags.slice(0, remainingLimit);
			selectedTags = [...extraTagsToInclude];
			remainingLimit -= extraTagsToInclude.length;
		} else {
			// For time limit, take extra tags until we reach the limit
			let totalDuration = 0;

			for (const tag of extraTags) {
				const duration = tag.secondsAfter + tag.secondsBefore;
				if (totalDuration + duration > remainingLimit) {
					break;
				}

				selectedTags.push(tag);
				totalDuration += duration;
			}

			remainingLimit -= totalDuration;
		}
	}

	// If we still have remaining limit, select more tags
	if (remainingLimit > 0) {
		let additionalTags = [];

		if (formulaConfig.distributeGamePeriods) {
			// For distributeGamePeriods, select from time sections
			additionalTags = selectTagsFromTimeSections(
				scoredTags,
				event.durationSeconds,
				limitType,
				remainingLimit,
				formulaConfig,
				event // Pass event for team balancing
			);
		} else {
			// If not distributing by periods, handle team balancing directly
			if (formulaConfig.balanceTeams === true && hasTeamDistribution(scoredTags)) {
				// Get the final score from the event
				const goalTeamA = event.finalScore.teamHomeScore || 0;
				const goalTeamB = event.finalScore.teamGuestScore || 0;

				// Use the balanceFactor from the formula configuration
				const factor = formulaConfig.balanceFactor || 0.5;

				// Use the score-based distribution
				additionalTags = selectTagsByScoreImbalance(scoredTags, limitType, remainingLimit, goalTeamA, goalTeamB, factor);
			} else {
				// Sort by score descending
				const sortedTags = [...scoredTags].sort((a, b) => b.calculatedScore - a.calculatedScore);

				// Select based on limit type
				if (limitType === 'clips') {
					additionalTags = sortedTags.slice(0, remainingLimit);
				} else {
					// Select tags until we reach the time limit
					let totalDuration = 0;

					for (const tag of sortedTags) {
						const duration = tag.secondsAfter + tag.secondsBefore;
						if (totalDuration + duration > remainingLimit) {
							break;
						}

						additionalTags.push(tag);
						totalDuration += duration;
					}
				}
			}
		}

		selectedTags = [...selectedTags, ...additionalTags];
	}

	// Sort by timestamp
	return selectedTags.sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Helper function to distribute tags between teams in a section
 * @param {Array} teamTags - Object mapping team IDs to their tags
 * @param {Number} quota - Number of tags to select
 * @returns {Array} Selected tags
 */
const distributeTagsBetweenTeams = (teamTags, quota) => {
	const teams = Object.keys(teamTags);
	if (teams.length === 0) return [];

	const selectedTags = [];
	let totalSelected = 0;

	if (teams.length === 1) {
		// Only one team, take top tags
		const teamId = teams[0];
		const tagsToTake = Math.min(quota, teamTags[teamId].length);
		selectedTags.push(...teamTags[teamId].slice(0, tagsToTake));
		totalSelected = tagsToTake;
	} else {
		// Multiple teams, distribute evenly
		const tagsPerTeam = Math.floor(quota / teams.length);
		// remaining if quota is not divisible by teams.length
		const extraTag = quota % teams.length;

		// Find team with next highest score for extra tag
		let extraTagTeam = null;
		if (extraTag > 0) {
			let highestScore = -1;
			teams.forEach(teamId => {
				if (teamTags[teamId].length <= tagsPerTeam) return;
				const nextTagScore = teamTags[teamId][tagsPerTeam].calculatedScore;
				if (nextTagScore > highestScore) {
					highestScore = nextTagScore;
					extraTagTeam = teamId;
				}
			});
		}

		// Select tags for each team
		teams.forEach(teamId => {
			const teamQuota = teamId === extraTagTeam ? tagsPerTeam + 1 : tagsPerTeam;
			const tagsToTake = Math.min(teamQuota, teamTags[teamId].length);
			selectedTags.push(...teamTags[teamId].slice(0, tagsToTake));
			totalSelected += tagsToTake;
		});
	}

	return { selectedTags, totalSelected };
};

/**
 * Helper function to distribute duration between teams in a section
 * @param {Object} teamTags - Object mapping team IDs to their tags
 * @param {Number} targetDuration - Target duration for the section
 * @returns {Object} Selected tags and total duration
 */
const distributeDurationBetweenTeams = (teamTags, targetDuration) => {
	const teams = Object.keys(teamTags);
	if (teams.length === 0) return { selectedTags: [], totalDuration: 0 };

	const selectedTags = [];
	let totalDuration = 0;

	if (teams.length === 1) {
		// Only one team, take tags until reaching duration
		const teamId = teams[0];
		for (const tag of teamTags[teamId]) {
			const duration = tag.secondsAfter + tag.secondsBefore;
			if (totalDuration + duration > targetDuration) break;
			selectedTags.push(tag);
			totalDuration += duration;
		}
	} else {
		// Multiple teams, distribute duration evenly
		const durationPerTeam = targetDuration / teams.length;

		// First round: select tags for each team up to their duration quota
		teams.forEach(teamId => {
			let teamDuration = 0;
			for (const tag of teamTags[teamId]) {
				const duration = tag.secondsAfter + tag.secondsBefore;
				if (teamDuration + duration > durationPerTeam) break;
				selectedTags.push(tag);
				teamDuration += duration;
				totalDuration += duration;
			}
		});
	}

	return { selectedTags, totalDuration };
};

/**
 * Helper function to fill remaining quota with highest scored tags
 * @param {Array} allTags - All available tags
 * @param {Set} selectedIds - Set of already selected tag IDs
 * @param {Number} remainingQuota - Remaining number of tags to select
 * @returns {Array} Additional selected tags
 */
const fillWithHighestScoredTags = (allTags, selectedIds, remainingQuota) => {
	const remainingTags = allTags
		.filter(tag => !selectedIds.has(tag.id))
		.sort((a, b) => b.calculatedScore - a.calculatedScore);
	return remainingTags.slice(0, remainingQuota);
};

/**
 * Helper function to fill remaining duration with highest scored tags
 * @param {Array} allTags - All available tags
 * @param {Set} selectedIds - Set of already selected tag IDs
 * @param {Number} remainingDuration - Remaining duration to fill
 * @returns {Object} Additional selected tags and total duration
 */
const fillWithHighestScoredDuration = (allTags, selectedIds, remainingDuration) => {
	const selectedTags = [];
	let totalDuration = 0;

	const remainingTags = allTags
		.filter(tag => !selectedIds.has(tag.id))
		.sort((a, b) => b.calculatedScore - a.calculatedScore);

	for (const tag of remainingTags) {
		const duration = tag.secondsAfter + tag.secondsBefore;
		if (totalDuration + duration > remainingDuration) break;
		selectedTags.push(tag);
		totalDuration += duration;
	}

	return { selectedTags, totalDuration };
};

/**
 * Helper function to create sections from tags
 * @param {Array} tags - Tags to organize into sections
 * @param {Number} numberOfSections - Number of sections to create
 * @param {Function} getSectionIndex - Function to determine section index for a tag
 * @returns {Object} Sections and team tags
 */
const createSections = (tags, numberOfSections, getSectionIndex) => {
	const sections = Array(numberOfSections).fill().map(() => []);
	const sectionTeamTags = Array(numberOfSections).fill().map(() => ({}));

	tags.forEach(tag => {
		if (!tag.teamId) return;

		const sectionIndex = getSectionIndex(tag);
		if (sectionIndex === -1) return;

		// Add to general section array
		sections[sectionIndex].push(tag);

		// Add to team-specific collection within section
		if (!sectionTeamTags[sectionIndex][tag.teamId]) {
			sectionTeamTags[sectionIndex][tag.teamId] = [];
		}
		sectionTeamTags[sectionIndex][tag.teamId].push(tag);
	});

	// Sort tags by score within each team in each section
	sections.forEach((section, index) => {
		const teamTags = sectionTeamTags[index];
		Object.keys(teamTags).forEach(teamId => {
			teamTags[teamId].sort((a, b) => b.calculatedScore - a.calculatedScore);
		});
		section.sort((a, b) => b.calculatedScore - a.calculatedScore);
	});

	return { sections, sectionTeamTags };
};

/**
 * Select tags from time sections (for Condensed highlight type)
 * @param {Array} scoredTags
 * @param {Number} eventDuration
 * @param {String} limitType
 * @param {Number} limitValue
 * @param {Object} formulaConfig
 * @returns {Array}
 */
const selectTagsFromTimeSections = (scoredTags, eventDuration, limitType, limitValue, formulaConfig, event) => {
	// If not using distributeGamePeriods or no period markers configured, use fallback
	if (!formulaConfig.distributeGamePeriods || !formulaConfig.periodMarkers?.length) {
		return selectTagsFromTimeSectionsFallback(scoredTags, eventDuration, limitType, limitValue, formulaConfig);
	}

	// First, calculate team quotas if balanceTeams is enabled
	let teamQuotas = null;
	if (formulaConfig.balanceTeams === true && hasTeamDistribution(scoredTags)) {
		const goalTeamA = event.finalScore.teamHomeScore || 0;
		const goalTeamB = event.finalScore.teamGuestScore || 0;
		const factor = formulaConfig.balanceFactor || 0.5;

		// Get unique team IDs from scored tags
		const teams = [...new Set(scoredTags.map(tag => tag.teamId).filter(Boolean))].sort();
		if (teams.length < 2) return [];

		if (limitType === 'clips') {
			const winningTeamClips = getTagCountForWinningTeam(limitValue, goalTeamA, goalTeamB, factor);
			const losingTeamClips = limitValue - winningTeamClips;
			teamQuotas = {
				winning:       winningTeamClips,
				losing:        losingTeamClips,
				winningTeamId: goalTeamA > goalTeamB ? teams[0] : teams[1],
				losingTeamId:  goalTeamA > goalTeamB ? teams[1] : teams[0],
			};
		} else {
			const winningTeamDuration = getTagCountForWinningTeam(limitValue, goalTeamA, goalTeamB, factor);
			const losingTeamDuration = limitValue - winningTeamDuration;
			teamQuotas = {
				winning:       winningTeamDuration,
				losing:        losingTeamDuration,
				winningTeamId: goalTeamA > goalTeamB ? teams[0] : teams[1],
				losingTeamId:  goalTeamA > goalTeamB ? teams[1] : teams[0],
			};
		}
	}

	const periods = [];

	// Process each period
	formulaConfig.periodMarkers.forEach((periodConfig) => {
		// Find period markers
		const periodStart = scoredTags.find(tag => tag.tag?.name === periodConfig.start);
		const periodEnd = scoredTags.find(tag => tag.tag?.name === periodConfig.end);

		if (!periodStart || !periodEnd) return;

		// Calculate period duration and block length
		const periodDuration = periodEnd.timestamp - periodStart.timestamp;
		const blockLength = periodDuration / formulaConfig.blocksPerPeriod;

		// Create blocks for this period
		const blocks = Array(formulaConfig.blocksPerPeriod).fill().map(() => ({
			tags:     [],
			teamTags: {},
		}));

		// Distribute tags into blocks
		scoredTags.forEach(tag => {
			if (!tag.teamId) return;

			// Skip if tag is outside period
			if (tag.timestamp < periodStart.timestamp || tag.timestamp > periodEnd.timestamp) return;

			// Calculate which block this tag belongs to
			const blockIndex = Math.min(
				Math.floor((tag.timestamp - periodStart.timestamp) / blockLength),
				formulaConfig.blocksPerPeriod - 1
			);

			// Add to block's general tags
			blocks[blockIndex].tags.push(tag);

			// Add to block's team-specific collection
			if (!blocks[blockIndex].teamTags[tag.teamId]) {
				blocks[blockIndex].teamTags[tag.teamId] = [];
			}
			blocks[blockIndex].teamTags[tag.teamId].push(tag);
		});

		// Sort tags by score within each team in each block
		blocks.forEach(block => {
			block.tags.sort((a, b) => b.calculatedScore - a.calculatedScore);
			Object.values(block.teamTags).forEach(teamTags => {
				teamTags.sort((a, b) => b.calculatedScore - a.calculatedScore);
			});
		});

		periods.push({
			start: periodStart,
			end:   periodEnd,
			blocks,
		});
	});

	// If no periods were created, use fallback
	if (periods.length === 0) {
		return selectTagsFromTimeSectionsFallback(scoredTags, eventDuration, limitType, limitValue, formulaConfig);
	}

	const selectedTags = [];
	const selectedIds = new Set();
	let unusedQuota = 0;

	if (teamQuotas) {
		// Calculate base clips/duration per block for each team
		const totalBlocks = periods.reduce((sum, period) => sum + period.blocks.length, 0);
		const winningBasePerBlock = Math.floor(teamQuotas.winning / totalBlocks);
		const losingBasePerBlock = Math.floor(teamQuotas.losing / totalBlocks);

		// Calculate remaining to distribute after base allocation
		let winningRemaining = teamQuotas.winning - (winningBasePerBlock * totalBlocks);
		let losingRemaining = teamQuotas.losing - (losingBasePerBlock * totalBlocks);

		// First pass: distribute base amount per block
		periods.forEach(period => {
			period.blocks.forEach(block => {
				// Process winning team
				const winningTeamTags = block.teamTags[teamQuotas.winningTeamId] || [];
				const winningSelected = selectFromBlock(
					winningTeamTags,
					winningBasePerBlock,
					limitType,
					selectedIds
				);
				selectedTags.push(...winningSelected.tags);
				winningSelected.tags.forEach(tag => selectedIds.add(tag.id));
				unusedQuota += winningBasePerBlock - winningSelected.count;

				// Process losing team
				const losingTeamTags = block.teamTags[teamQuotas.losingTeamId] || [];
				const losingSelected = selectFromBlock(
					losingTeamTags,
					losingBasePerBlock,
					limitType,
					selectedIds
				);
				selectedTags.push(...losingSelected.tags);
				losingSelected.tags.forEach(tag => selectedIds.add(tag.id));
				unusedQuota += losingBasePerBlock - losingSelected.count;
			});
		});

		// Add unused quota to remaining
		winningRemaining += unusedQuota * (teamQuotas.winning / (teamQuotas.winning + teamQuotas.losing));
		losingRemaining += unusedQuota * (teamQuotas.losing / (teamQuotas.winning + teamQuotas.losing));

		// Second pass: fill remaining quota with highest scored tags
		const remainingWinningTags = scoredTags
			.filter(tag =>
				!selectedIds.has(tag.id) &&
				tag.teamId === teamQuotas.winningTeamId
			)
			.sort((a, b) => b.calculatedScore - a.calculatedScore);

		const remainingLosingTags = scoredTags
			.filter(tag =>
				!selectedIds.has(tag.id) &&
				tag.teamId === teamQuotas.losingTeamId
			)
			.sort((a, b) => b.calculatedScore - a.calculatedScore);

		// Select remaining tags
		if (limitType === 'clips') {
			selectedTags.push(...remainingWinningTags.slice(0, Math.round(winningRemaining)));
			selectedTags.push(...remainingLosingTags.slice(0, Math.round(losingRemaining)));
		} else {
			let winningDuration = 0;
			let losingDuration = 0;

			for (const tag of remainingWinningTags) {
				const duration = tag.secondsAfter + tag.secondsBefore;
				if (winningDuration + duration > winningRemaining) break;
				selectedTags.push(tag);
				winningDuration += duration;
			}

			for (const tag of remainingLosingTags) {
				const duration = tag.secondsAfter + tag.secondsBefore;
				if (losingDuration + duration > losingRemaining) break;
				selectedTags.push(tag);
				losingDuration += duration;
			}
		}
	} else {
		// If not balancing teams, distribute evenly across blocks
		const tagsPerBlock = Math.floor(limitValue / (periods.length * formulaConfig.blocksPerPeriod));

		periods.forEach(period => {
			period.blocks.forEach(block => {
				const selected = selectFromBlock(
					block.tags,
					tagsPerBlock,
					limitType,
					selectedIds
				);
				selectedTags.push(...selected.tags);
				selected.tags.forEach(tag => selectedIds.add(tag.id));
				unusedQuota += tagsPerBlock - selected.count;
			});
		});

		// Fill remaining quota with highest scored tags
		const remainingTags = scoredTags
			.filter(tag => !selectedIds.has(tag.id))
			.sort((a, b) => b.calculatedScore - a.calculatedScore);

		if (limitType === 'clips') {
			selectedTags.push(...remainingTags.slice(0, unusedQuota));
		} else {
			let totalDuration = 0;
			for (const tag of remainingTags) {
				const duration = tag.secondsAfter + tag.secondsBefore;
				if (totalDuration + duration > unusedQuota) break;
				selectedTags.push(tag);
				totalDuration += duration;
			}
		}
	}

	return selectedTags.sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Helper function to select tags from a block
 * @param {Array} tags - Tags to select from
 * @param {Number} limit - Number of tags/seconds to select
 * @param {String} limitType - 'clips' or 'seconds'
 * @param {Set} selectedIds - Set of already selected tag IDs
 * @returns {Object} Selected tags and count
 */
const selectFromBlock = (tags, limit, limitType, selectedIds) => {
	const selected = [];
	let count = 0;

	if (limitType === 'clips') {
		for (const tag of tags) {
			if (count >= limit || selectedIds.has(tag.id)) continue;
			selected.push(tag);
			count++;
		}
	} else {
		let totalDuration = 0;
		for (const tag of tags) {
			if (selectedIds.has(tag.id)) continue;
			const duration = tag.secondsAfter + tag.secondsBefore;
			if (totalDuration + duration > limit) break;
			selected.push(tag);
			totalDuration += duration;
			count = totalDuration;
		}
	}

	return { tags: selected, count };
};

/**
 * Fallback implementation using fixed-length blocks (10 minutes by default)
 * @param {Array} scoredTags
 * @param {Number} eventDuration
 * @param {String} limitType
 * @param {Number} limitValue
 * @param {Object} formulaConfig
 * @returns {Array}
 */
const selectTagsFromTimeSectionsFallback = (scoredTags, eventDuration, limitType, limitValue, formulaConfig) => {
	// Use fallback block length (10 minutes) or calculate based on event duration
	const blockLength = formulaConfig.fallbackBlockLength || 10 * 60;
	const numberOfSections = Math.ceil(eventDuration / blockLength);

	// Create sections
	const { sections, sectionTeamTags } = createSections(
		scoredTags,
		numberOfSections,
		tag => Math.min(Math.floor(tag.timestamp / blockLength), numberOfSections - 1)
	);

	const selectedTags = [];
	let totalSelected = 0;
	let totalDuration = 0;

	if (limitType === 'clips') {
		// Calculate tags per section
		const tagsPerSection = Math.floor(limitValue / sections.length);

		// Process each section
		for (let i = 0; i < sections.length; i++) {
			const { selectedTags: sectionTags, totalSelected: sectionSelected } = distributeTagsBetweenTeams(
				sectionTeamTags[i],
				tagsPerSection
			);
			selectedTags.push(...sectionTags);
			totalSelected += sectionSelected;
		}

		// Fill remaining quota if needed
		if (totalSelected < limitValue) {
			const selectedIds = new Set(selectedTags.map(tag => tag.id));
			const additionalTags = fillWithHighestScoredTags(
				scoredTags,
				selectedIds,
				limitValue - totalSelected
			);
			selectedTags.push(...additionalTags);
		}
	} else {
		// Duration-based selection
		const durationPerSection = limitValue / sections.length;

		// Process each section
		for (let i = 0; i < sections.length; i++) {
			const { selectedTags: sectionTags, totalDuration: sectionDuration } = distributeDurationBetweenTeams(
				sectionTeamTags[i],
				durationPerSection
			);
			selectedTags.push(...sectionTags);
			totalDuration += sectionDuration;
		}

		// Fill remaining duration if needed
		if (totalDuration < limitValue) {
			const selectedIds = new Set(selectedTags.map(tag => tag.id));
			const { selectedTags: additionalTags, totalDuration: additionalDuration } = fillWithHighestScoredDuration(
				scoredTags,
				selectedIds,
				limitValue - totalDuration
			);
			selectedTags.push(...additionalTags);
			totalDuration += additionalDuration;
		}
	}

	return selectedTags.sort((a, b) => a.timestamp - b.timestamp);
};
